import React, { useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebaseConfig";
import { buildReturnPath } from "../../utils/buildReturnPath";
import { launchConfetti } from "../../utils/confetti";
import "./UniversalMathGame.css";
import { useLeaveGame } from "./gameExit";
import "./kit/quizkit.css"; // .qk-scene: the shared game background (no pirate ship — that's the bonus round's)
import "./kit/classicGames.css"; // the new look for this game (buttons, cards, score chips)

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";

const MUSIC_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fbonus-round1.mp3?alt=media&token=e4c606bb-90ca-4a78-9f2e-34a709084464";

const CORRECT_SOUND =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";

const WRONG_SOUND =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";

const DEFAULT_BG =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fpirate-ship-background.png?alt=media&token=3912623d-30d2-468a-ab2e-449398d2bce2";

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const normalizeMode = (mode = "") =>
  String(mode).toLowerCase().replace(/[^a-z0-9]/g, "");

const buildProblem = ({ mode, value }) => {
  const normalized = normalizeMode(mode);

  if (normalized === "mathaddition" || normalized === "addition") {
    const a = randInt(0, value);
    const b = randInt(0, value);
    return {
      question: `${a} + ${b}`,
      answer: a + b,
      speak: `What is ${a} plus ${b}?`,
    };
  }

  if (normalized === "mathsubtraction" || normalized === "subtraction") {
    const a = randInt(value, value + 10);
    const b = randInt(0, value);
    return {
      question: `${a} - ${b}`,
      answer: a - b,
      speak: `What is ${a} minus ${b}?`,
    };
  }

  const b = randInt(0, 12);
  return {
    question: `${value} × ${b}`,
    answer: value * b,
    speak: `What is ${value} times ${b}?`,
  };
};

const buildChoices = (correctAnswer) => {
  const choices = new Set([correctAnswer]);

  while (choices.size < 4) {
    const offset = randInt(-12, 12);
    const candidate = Math.max(0, correctAnswer + offset);

    if (candidate !== correctAnswer) {
      choices.add(candidate);
    }
  }

  return shuffle([...choices]);
};

const getTitle = ({ mode, value }) => {
  const normalized = normalizeMode(mode);

  if (normalized === "mathaddition" || normalized === "addition") {
    return "➕ Addition Adventure";
  }

  if (normalized === "mathsubtraction" || normalized === "subtraction") {
    return "➖ Subtraction Quest";
  }

  return `✖️ ${value} Times Table Game`;
};

const UniversalMathGame = ({
  mode = "mathTimesTable",
  value = 5,
  questions = [],
  onComplete,
  user,
  topic,
  placeholderImage,
}) => {
  const navigate = useNavigate();
  const leaveGame = useLeaveGame(); // back to the subject page this game came from
  const { subject } = useParams();

  const totalQuestions = Number(questions?.[0]?.totalQuestions || 10);
  const mathValue = Number(value || questions?.[0]?.value || 5);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [problem, setProblem] = useState(() =>
    buildProblem({ mode, value: mathValue })
  );
  const [choices, setChoices] = useState(() =>
    buildChoices(problem.answer)
  );
  const [score, setScore] = useState(0);
  const [details, setDetails] = useState([]);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [message, setMessage] = useState("Pick the correct answer!");
  const [isMuted, setIsMuted] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const musicRef = useRef(null);
  const correctRef = useRef(null);
  const wrongRef = useRef(null);

  const title = useMemo(
    () => getTitle({ mode, value: mathValue }),
    [mode, mathValue]
  );

  useEffect(() => {
    musicRef.current = new Audio(MUSIC_URL);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.22;
    musicRef.current.play().catch(() => {});

    correctRef.current = new Audio(CORRECT_SOUND);
    wrongRef.current = new Audio(WRONG_SOUND);

    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.removeAttribute("src");
        musicRef.current.load();
      }

      window.speechSynthesis?.cancel();
    };
  }, []);

  useEffect(() => {
    if (musicRef.current) {
      musicRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);

    if (musicRef.current && !isMuted) {
      musicRef.current.volume = 0.22;
    }
  };

  const speak = (text) => {
    if (!voiceOn || !window.speechSynthesis || !text) return;

    stopSpeaking();

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "en-US";
    utter.rate = 0.9;
    utter.pitch = 1;

    const voices = window.speechSynthesis.getVoices?.() || [];
    const preferred =
      voices.find((v) => /en-US/i.test(v.lang) && /Google|Samantha|Jenny|Aria/i.test(v.name)) ||
      voices.find((v) => /en-US/i.test(v.lang)) ||
      voices[0];

    if (preferred) utter.voice = preferred;

    if (musicRef.current) {
      musicRef.current.volume = 0.08;
    }

    setIsSpeaking(true);

    utter.onend = () => {
      setIsSpeaking(false);
      if (musicRef.current && !isMuted) musicRef.current.volume = 0.22;
    };

    utter.onerror = () => {
      setIsSpeaking(false);
      if (musicRef.current && !isMuted) musicRef.current.volume = 0.22;
    };

    window.speechSynthesis.speak(utter);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      speak(problem.speak);
    }, 250);

    return () => clearTimeout(timer);
  }, [currentIndex, voiceOn]);

  const nextProblem = () => {
    const newProblem = buildProblem({ mode, value: mathValue });
    setProblem(newProblem);
    setChoices(buildChoices(newProblem.answer));
    setSelectedChoice(null);
    setMessage("Pick the correct answer!");
  };

  const finishGame = (finalScore, finalDetails) => {
    const passingScore = Math.ceil(totalQuestions * 0.8);

    if (finalScore >= passingScore) {
      launchConfetti();
    }

    onComplete?.({
      score: finalScore,
      answers: finalDetails,
      details: finalDetails,
    });
  };

  const handleChoice = (choice) => {
    if (selectedChoice !== null) return;

    stopSpeaking();

    const isCorrect = Number(choice) === Number(problem.answer);
    const nextScore = score + (isCorrect ? 1 : 0);

    setSelectedChoice(choice);

    if (isCorrect) {
      playGameSound("correct", "universalMath", () => correctRef.current?.play().catch(() => {}));
      setMessage("Great job!");
      speak("Great job!");
    } else {
      playGameSound("wrong", "universalMath", () => wrongRef.current?.play().catch(() => {}));
      setMessage(`Good try! The answer was ${problem.answer}.`);
      speak(`Good try. The answer was ${problem.answer}.`);
    }

    const nextDetails = [
      ...details,
      {
        qIndex: currentIndex,
        question: problem.question,
        selectedText: String(choice),
        correctText: String(problem.answer),
        correct: isCorrect,
      },
    ];

    setDetails(nextDetails);
    setScore(nextScore);

    setTimeout(() => {
      if (currentIndex + 1 >= totalQuestions) {
        finishGame(nextScore, nextDetails);
      } else {
        setCurrentIndex((prev) => prev + 1);
        nextProblem();
      }
    }, 1200);
  };

  const handleReturnHome = () => leaveGame();

  return (
    <div
      className="universal-math-game-wrapper qk-scene"
    >
      <div className="math-game-top-controls">
        <button type="button" onClick={() => setIsMuted((prev) => !prev)}>
          {isMuted ? "🔇" : "🔊"}
        </button>

        <button type="button" onClick={() => setVoiceOn((prev) => !prev)}>
          {voiceOn ? "🗣️ Voice On" : "🤫 Voice Off"}
        </button>

        <button type="button" onClick={() => speak(problem.speak)}>
          🔊 Speak
        </button>

        {isSpeaking && (
          <button type="button" onClick={stopSpeaking}>
            ⏹ Stop
          </button>
        )}

        <button type="button" onClick={handleReturnHome}>
          🏠 Home
        </button>
      </div>

      <div className="universal-math-game-card">
        <img src={LOGO_URL} alt="SBL Logo" className="math-game-logo" />

        <h1>{title}</h1>

        <div className="math-game-progress">
          <span>⭐ Score: {score}</span>
          <span>
            Question: {currentIndex + 1}/{totalQuestions}
          </span>
        </div>

        <motion.div
          key={problem.question}
          className="math-game-question"
          initial={{ scale: 0.8, rotate: -2 }}
          animate={{ scale: 1, rotate: 0 }}
        >
          {problem.question}
        </motion.div>

        <p className="math-game-message">{message}</p>

        <div className="math-choice-grid">
          {choices.map((choice) => {
            const wasSelected = selectedChoice === choice;
            const isCorrect = choice === problem.answer;

            return (
              <motion.button
                key={choice}
                type="button"
                className={`math-choice-button ${
                  wasSelected ? (isCorrect ? "correct" : "incorrect") : ""
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleChoice(choice)}
              >
                {choice}
              </motion.button>
            );
          })}
        </div>

        <div className="math-game-footer">
          © Smart Board Learning LLC. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default UniversalMathGame;